'use client';

import Link from 'next/link';
import type { CSSProperties } from 'react';
import { coverBackground, type LearningItem } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { LearningRailFrame } from './_LearningRailFrame';
import { cardReset } from '../_shared/styles';

/**
 * Auto-scrolling marquee of catalog items. Items split alternately
 * into rows so a 12-item fetch fills two rows of 6 with no gap.
 *
 * <p>Pure CSS animation (no JS RAF loop). Pauses on hover.
 */
export interface AutoScrollMarqueeProps {
  title?: string;
  subtitle?: string;
  rows?: 1 | 2;
  source?: string;
  speed?: 'slow' | 'medium' | 'fast';
  cardStyle?: 'course' | 'instructor';
  courseHrefTemplate?: string;
}

const SOURCE_ALIAS: Record<string, string> = {
  popular: 'popular_courses',
  featured: 'featured_courses',
  newest: 'newly_published_courses',
};

const SPEED_DURATION: Record<string, string> = {
  slow: '60s',
  medium: '40s',
  fast: '25s',
};

export function AutoScrollMarquee({
  title,
  subtitle,
  rows = 2,
  source: rawSource,
  speed = 'medium',
  cardStyle = 'course',
  courseHrefTemplate = '/lxp/course/{id}',
}: AutoScrollMarqueeProps) {
  const trimmed = (rawSource ?? '').trim() || 'popular_courses';
  const source = SOURCE_ALIAS[trimmed] ?? trimmed;
  const duration = SPEED_DURATION[speed] ?? '40s';
  return (
    <LearningRailFrame
      blockType="AutoScrollMarquee"
      source={source}
      limit={12}
      title={title}
      subtitle={subtitle}
      skeletonLayout="rail"
      emptyTitle="No items to scroll"
    >
      {(items) => {
        const rowItems: LearningItem[][] =
          Number(rows) === 2
            ? [items.filter((_, i) => i % 2 === 0), items.filter((_, i) => i % 2 === 1)]
            : [items];
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <style dangerouslySetInnerHTML={{ __html: MARQUEE_CSS }} />
            {rowItems.map((row, rowIdx) => (
              <div
                key={rowIdx}
                className="sb-marquee-track"
                style={{ '--duration': duration } as CSSProperties}
              >
                <div className="sb-marquee-content">
                  {[...row, ...row].map((item, i) => (
                    <Card asChild key={`${item.id}-${i}`}>
                      <Link
                        href={courseHrefTemplate.replace('{id}', encodeURIComponent(item.id))}
                        style={{
                          ...cardReset,
                          flex: '0 0 220px',
                          background: 'var(--color-surface-card, #fff)',
                          borderRadius: 'var(--radius-md, 8px)',
                          overflow: 'hidden',
                          textDecoration: 'none',
                          color: 'inherit',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <div
                          aria-hidden
                          style={{
                            aspectRatio:
                              cardStyle === 'instructor'
                                ? 'var(--aspect-ratio-instructor-card, 1 / 1)'
                                : 'var(--aspect-ratio-course-card, 16 / 10)',
                            ...coverBackground(item.imageUrl),
                          }}
                        />
                        <div
                          style={{
                            padding: 10,
                            fontSize: '13px',
                            fontWeight: 600,
                            lineHeight: 1.3,
                          }}
                        >
                          {item.title}
                        </div>
                      </Link>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      }}
    </LearningRailFrame>
  );
}

const MARQUEE_CSS = `
  .sb-marquee-track {
    overflow: hidden;
    position: relative;
  }
  .sb-marquee-content {
    display: flex;
    gap: 12px;
    width: max-content;
    animation: sb-marquee var(--duration, 40s) linear infinite;
  }
  .sb-marquee-track:hover .sb-marquee-content {
    animation-play-state: paused;
  }
  @keyframes sb-marquee {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }
  @media (prefers-reduced-motion: reduce) {
    .sb-marquee-content { animation: none; }
  }
`;
